/**
 * SignedInUsersTable — actual /api/users rows with suspend / unsuspend /
 * impersonate controls.
 */
import React from "react";
import { ShieldOff, ShieldCheck, UserCog } from "lucide-react";
import { fmtDate } from "./helpers";

const ROLE_LABELS = {
  founder: "Founder",
  sales_manager: "Sales manager",
  sales_person: "Sales person",
  trial_user: "Trial user",
  campaign_user: "Campaign user",
  lite_public: "Public",
};
const ADMIN_ROLES = new Set(["founder", "sales_manager", "sales_person"]);

function RoleBadge({ role, status }) {
  const label = ROLE_LABELS[role] || role || "—";
  const isAdmin = ADMIN_ROLES.has(role);
  const cls = isAdmin
    ? "bg-[#F1EAFE] text-[#5D28C7]"
    : "bg-zinc-100 text-zinc-600";
  return (
    <span className="inline-flex flex-col gap-0.5" title={isAdmin ? "Has admin dashboard access (incl. Assessments)" : "Tool surface only — no admin menu"}>
      <span className={`inline-block ${cls} text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5`}>{label}</span>
      {status && status !== "active" && (
        <span className="text-[9.5px] uppercase tracking-wide text-amber-600">{status}</span>
      )}
    </span>
  );
}

export default function SignedInUsersTable({ users, me, currentMe, onImpersonate, onSuspend, onUnsuspend }) {
  return (
    <section data-testid="users-logged-in-section">
      <h3 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-zinc-500 mb-3">
        Signed-in users <span className="text-zinc-400 normal-case tracking-normal">({users.length})</span>
      </h3>
      {users.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-8 text-center">
          <p className="text-zinc-600 text-[14px]">No one has signed in yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-zinc-200">
          <table className="min-w-full text-[13px]">
            <thead className="bg-zinc-50 text-left text-zinc-500 text-[11px] uppercase tracking-[0.12em]">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role / access</th>
                <th className="p-3">State</th>
                <th className="p-3">Last sign-in</th>
                <th className="p-3">First sign-in</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = (u.email || "").toLowerCase() === me;
                return (
                  <tr key={u.user_id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3 font-medium text-zinc-800">
                      {u.name || "—"}
                      {isMe ? <span className="ml-2 text-[10px] uppercase tracking-wide bg-[#7737F6] text-white px-1.5 py-0.5">You</span> : null}
                    </td>
                    <td className="p-3 font-mono text-zinc-700">{u.email}</td>
                    <td className="p-3" data-testid={`user-role-${u.user_id}`}>
                      <RoleBadge role={u.platform_role} status={u.platform_status} />
                    </td>
                    <td className="p-3">
                      {u.currently_allowed ? (
                        <span className="inline-block bg-emerald-100 text-emerald-700 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5">Active</span>
                      ) : (
                        <span className="inline-block bg-red-100 text-red-700 text-[10.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5" title={u.allow_reason}>Blocked</span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap text-zinc-600">{fmtDate(u.last_login_at)}</td>
                    <td className="p-3 whitespace-nowrap text-zinc-600">{fmtDate(u.created_at)}</td>
                    <td className="p-3 text-right">
                      {isMe ? (
                        <span className="text-zinc-400 text-[11.5px]">—</span>
                      ) : (
                        <div className="flex items-center justify-end gap-3">
                          {currentMe?.ui_flags?.show_impersonate && u.currently_allowed && (
                            <button
                              type="button"
                              onClick={() => onImpersonate(u)}
                              className="text-[12px] text-[#7737F6] hover:text-[#5e2bcc] inline-flex items-center gap-1"
                              data-testid={`user-impersonate-${u.user_id}`}
                              title="Act as this user — audit-logged, founder-only"
                            >
                              <UserCog size={12} /> Impersonate
                            </button>
                          )}
                          {u.currently_allowed ? (
                            <button
                              type="button"
                              onClick={() => onSuspend(u)}
                              className="text-[12px] text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                              data-testid={`user-suspend-${u.user_id}`}
                            >
                              <ShieldOff size={12} /> Suspend
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => onUnsuspend(u)}
                              className="text-[12px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                              data-testid={`user-unsuspend-${u.user_id}`}
                            >
                              <ShieldCheck size={12} /> Restore
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
